/**
 * OnboardingProvider — Context provider for tour management.
 *
 * Ported from FlexShell's OnboardingProvider.
 * Uses driver.js for the actual tour UI.
 * Integrates with Thread's ProvenanceContext (setOpenPanel) to
 * focus the SlidePanel on the correct tab before highlighting.
 */

import { createContext, useCallback, useRef, useState, useEffect, type ReactNode } from 'react';
import { driver, type Driver, type DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';
import './onboarding-theme.css';

import type { TourId, TourStep, OnboardingConfig, OnboardingContextValue, Challenge, StartTourOptions, TourEvent } from './types';
import {
  getChallenge,
  isPanelVisible,
  isElementVisible,
  getPanelDisplayName,
  getParentPanel,
  buildElementNotVisibleHTML,
  registerChallenges,
  clearChallenges,
} from './challenges';
import {
  markTourComplete,
  hasCompletedTour as checkTourCompleted,
  resetOnboarding,
  saveTourProgress,
  getTourProgress,
  clearTourProgress,
  hasTourProgress,
  setStoragePrefix,
} from './storage';
import type { PanelTab } from '../../context/ProvenanceContext';

export const OnboardingContext = createContext<OnboardingContextValue | null>(null);

interface OnboardingProviderProps {
  children: ReactNode;
  config?: OnboardingConfig;
  /** Callback to open/close a panel tab — wired to setOpenPanel from ProvenanceContext */
  onOpenPanel?: (panelId: string | null) => void;
}

export function OnboardingProvider({ children, config, onOpenPanel }: OnboardingProviderProps) {
  const [isActive, setIsActive] = useState(false);
  const [currentTour, setCurrentTour] = useState<TourId | null>(null);
  const driverRef = useRef<Driver | null>(null);
  const challengeIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const currentChallengeRef = useRef<Challenge | null>(null);
  const stopAfterStepRef = useRef<number | undefined>(undefined);
  const tourEventHandlerRef = useRef<((event: TourEvent) => void) | null>(null);

  const openPanel = useCallback((panelId: string) => {
    onOpenPanel?.(panelId as PanelTab & string);
  }, [onOpenPanel]);

  const tours = config?.tours ?? {};

  useEffect(() => {
    if (config?.storagePrefix) {
      setStoragePrefix(config.storagePrefix);
    }
  }, [config?.storagePrefix]);

  useEffect(() => {
    if (config?.challenges) {
      clearChallenges();
      registerChallenges(Object.values(config.challenges));
    }
  }, [config?.challenges]);

  const clearChallengePolling = useCallback(() => {
    if (challengeIntervalRef.current) {
      clearInterval(challengeIntervalRef.current);
      challengeIntervalRef.current = null;
    }
    currentChallengeRef.current = null;
  }, []);

  const buildChallengeHTML = useCallback((challenge: Challenge, isComplete: boolean) => {
    const panelVisible = challenge.requiredPanel ? isPanelVisible(challenge.requiredPanel) : true;
    const panelName = challenge.requiredPanel ? getPanelDisplayName(challenge.requiredPanel) : '';

    const panelWarning = !panelVisible && challenge.requiredPanel ? `
      <div class="tour-challenge__panel-warning">
        <span class="tour-challenge__icon">⚠</span>
        <span>First, open the <strong>${panelName}</strong> panel</span>
      </div>
    ` : '';

    return `
      <div class="tour-challenge ${isComplete ? 'tour-challenge--complete' : ''}">
        <div class="tour-challenge__header">
          <span class="tour-challenge__icon">${isComplete ? '✓' : '→'}</span>
          <span class="tour-challenge__label">${isComplete ? 'Complete!' : 'Try it'}</span>
        </div>
        ${panelWarning}
        <div class="tour-challenge__instruction">${challenge.instruction}</div>
        ${challenge.targetValue ? `<div class="tour-challenge__target">Target: <strong>${challenge.targetValue}</strong></div>` : ''}
        ${challenge.hint && !isComplete ? `<div class="tour-challenge__hint">${challenge.hint}</div>` : ''}
        ${isComplete ? '<div class="tour-challenge__success">Great job! Click Next to continue.</div>' : ''}
      </div>
    `;
  }, []);

  const convertToDriverSteps = useCallback((tourSteps: TourStep[]): DriveStep[] => {
    return tourSteps.map((step) => {
      const challenge = step.challengeId ? getChallenge(step.challengeId) : null;

      let description = step.popover?.description ?? '';
      if (challenge) {
        const isComplete = challenge.validate();
        description = `${description}${buildChallengeHTML(challenge, isComplete)}`;
      }

      return {
        element: step.element,
        popover: {
          ...step.popover,
          description,
        },
      };
    });
  }, [buildChallengeHTML]);

  const emitTourEvent = useCallback((event: TourEvent) => {
    tourEventHandlerRef.current?.(event);
  }, []);

  const getDriverInstance = useCallback((tourSteps: TourStep[], tourId: TourId) => {
    if (driverRef.current) {
      driverRef.current.destroy();
    }
    clearChallengePolling();

    driverRef.current = driver({
      showProgress: true,
      showButtons: ['next', 'previous', 'close'],
      nextBtnText: 'Next',
      prevBtnText: 'Back',
      doneBtnText: 'Done',
      progressText: '{{current}} of {{total}}',
      allowClose: true,
      stagePadding: 8,
      stageRadius: 8,
      popoverClass: 'thread-tour-popover',

      onHighlightStarted: () => {
        clearChallengePolling();

        const stepIndex = driverRef.current?.getActiveIndex() ?? 0;
        const originalStep = tourSteps[stepIndex];

        // Check stop boundary before rendering this step
        const stopAfter = stopAfterStepRef.current;
        if (stopAfter !== undefined && stepIndex > stopAfter) {
          saveTourProgress(tourId, stepIndex);
          setTimeout(() => {
            driverRef.current?.destroy();
            emitTourEvent({ type: 'stopped', tourId, step: stopAfter, reason: 'stop-point' });
          }, 0);
          return;
        }

        saveTourProgress(tourId, stepIndex);
        emitTourEvent({ type: 'step', tourId, step: stepIndex });

        const elementSelector = originalStep?.element as string | undefined;
        const parentPanel = originalStep?.focusPanel || (elementSelector ? getParentPanel(elementSelector) : null);

        if (parentPanel === 'close') {
          onOpenPanel?.(null);
        } else if (parentPanel) {
          openPanel(parentPanel);
        }

        if (originalStep?.onBeforeHighlight) {
          originalStep.onBeforeHighlight();
        }

        const challenge = originalStep?.challengeId ? (getChallenge(originalStep.challengeId) ?? null) : null;

        if (challenge || elementSelector) {
          currentChallengeRef.current = challenge;

          let lastPanelVisible = challenge?.requiredPanel ? isPanelVisible(challenge.requiredPanel) : true;
          let lastComplete = challenge?.validate() ?? true;
          let lastElementVisible = elementSelector ? isElementVisible(elementSelector) : true;

          challengeIntervalRef.current = setInterval(() => {
            const panelVisible = challenge?.requiredPanel ? isPanelVisible(challenge.requiredPanel) : true;
            const isComplete = challenge?.validate() ?? true;
            const elementVisible = elementSelector ? isElementVisible(elementSelector) : true;

            if (challenge && (panelVisible !== lastPanelVisible || isComplete !== lastComplete)) {
              lastPanelVisible = panelVisible;
              lastComplete = isComplete;

              const popoverEl = document.querySelector('.driver-popover-description');
              if (popoverEl && originalStep?.popover) {
                const baseDesc = originalStep.popover.description ?? '';
                const warningHTML = !elementVisible ? buildElementNotVisibleHTML(parentPanel) : '';
                popoverEl.innerHTML = `${warningHTML}${baseDesc}${buildChallengeHTML(challenge, isComplete)}`;
              }

              const nextBtn = document.querySelector('.driver-popover-next-btn') as HTMLButtonElement;
              if (nextBtn) {
                nextBtn.disabled = !isComplete;
                nextBtn.classList.toggle('driver-popover-btn--disabled', !isComplete);
              }
            }

            if (elementVisible !== lastElementVisible) {
              lastElementVisible = elementVisible;
              driverRef.current?.refresh();
            }

            if (challenge && isComplete && elementVisible) {
              clearChallengePolling();
            }
          }, 200);
        }
      },

      onPopoverRender: (popover) => {
        const stepIndex = driverRef.current?.getActiveIndex() ?? 0;
        const originalStep = tourSteps[stepIndex];

        if (originalStep?.challengeId) {
          const challenge = getChallenge(originalStep.challengeId);
          if (challenge && !challenge.validate()) {
            const footer = popover.footerButtons;
            const nextBtn = footer.querySelector('.driver-popover-next-btn') as HTMLButtonElement;

            if (nextBtn) {
              if (!challenge.noSkip) {
                const skipBtn = document.createElement('button');
                skipBtn.className = 'driver-popover-skip-btn';
                skipBtn.textContent = 'Skip';
                skipBtn.onclick = () => {
                  clearChallengePolling();
                  driverRef.current?.moveNext();
                };
                nextBtn.parentNode?.insertBefore(skipBtn, nextBtn);
              }

              nextBtn.disabled = true;
              nextBtn.classList.add('driver-popover-btn--disabled');
            }
          }
        }
      },

      onDestroyed: () => {
        clearChallengePolling();
        const savedProgress = getTourProgress(tourId);
        if (savedProgress >= tourSteps.length - 1) {
          markTourComplete(tourId);
          clearTourProgress(tourId);
          emitTourEvent({ type: 'complete', tourId });
        }
        stopAfterStepRef.current = undefined;
        setIsActive(false);
        setCurrentTour(null);
      },

      onCloseClick: () => {
        const currentStepIndex = driverRef.current?.getActiveIndex() ?? 0;
        saveTourProgress(currentTour!, currentStepIndex);
        emitTourEvent({ type: 'stopped', tourId, step: currentStepIndex, reason: 'user-close' });
        driverRef.current?.destroy();
      },
    });

    return driverRef.current;
  }, [clearChallengePolling, buildChallengeHTML, openPanel, currentTour, emitTourEvent]);

  useEffect(() => {
    return () => {
      clearChallengePolling();
      if (driverRef.current) {
        driverRef.current.destroy();
      }
    };
  }, [clearChallengePolling]);

  const startTour = useCallback((tourId: TourId, optionsOrFromBeginning?: StartTourOptions | boolean) => {
    if (isActive) return;

    const tourSteps = tours[tourId];
    if (!tourSteps) {
      console.warn(`Tour "${tourId}" not found`);
      return;
    }

    const options: StartTourOptions = typeof optionsOrFromBeginning === 'boolean'
      ? { fromBeginning: optionsOrFromBeginning }
      : optionsOrFromBeginning ?? {};

    if (options.fromBeginning) {
      clearTourProgress(tourId);
    }

    if (!hasTourProgress(tourId)) {
      saveTourProgress(tourId, 0);
    }

    stopAfterStepRef.current = options.stopAfterStep;
    setCurrentTour(tourId);
    setIsActive(true);

    setTimeout(() => {
      const driverInstance = getDriverInstance(tourSteps, tourId);
      const driverSteps = convertToDriverSteps(tourSteps);
      driverInstance.setSteps(driverSteps);

      const resolvedStart = options.fromStep
        ?? (options.fromBeginning ? 0 : getTourProgress(tourId));
      const startStep = Math.min(resolvedStart, tourSteps.length - 1);

      driverInstance.drive(startStep);
    }, 150);
  }, [getDriverInstance, convertToDriverSteps, isActive, tours]);

  const endTour = useCallback(() => {
    clearChallengePolling();
    if (driverRef.current) {
      if (currentTour) {
        const currentStepIndex = driverRef.current.getActiveIndex() ?? 0;
        saveTourProgress(currentTour, currentStepIndex);
      }
      driverRef.current.destroy();
    }
    setIsActive(false);
    setCurrentTour(null);
  }, [currentTour, clearChallengePolling]);

  const hasCompletedTour = useCallback((tourId: TourId): boolean => {
    return checkTourCompleted(tourId);
  }, []);

  const hasStartedTour = useCallback((tourId: TourId): boolean => {
    return checkTourCompleted(tourId) || hasTourProgress(tourId);
  }, []);

  const resetAllTours = useCallback(() => {
    resetOnboarding();
  }, []);

  const setTourEventHandler = useCallback((handler: ((event: TourEvent) => void) | null) => {
    tourEventHandlerRef.current = handler;
  }, []);

  const getStopPoints = useCallback((tourId: TourId): number[] => {
    const tourSteps = tours[tourId];
    if (!tourSteps) return [];
    return tourSteps.reduce<number[]>((acc, step, i) => {
      if (step.stopPoint) acc.push(i);
      return acc;
    }, []);
  }, [tours]);

  const contextValue: OnboardingContextValue = {
    startTour,
    endTour,
    hasCompletedTour,
    hasStartedTour,
    resetAllTours,
    isActive,
    currentTour,
    setTourEventHandler,
    getStopPoints,
  };

  return (
    <OnboardingContext.Provider value={contextValue}>
      {children}
    </OnboardingContext.Provider>
  );
}
